import {ConfiguratorDefinition} from '@fixtuur/backpack/src/api/configuratorDefinition';
import * as TE from 'fp-ts/TaskEither';
import {pipe} from 'fp-ts/lib/function';
import {Dispatch} from 'redux';
import {Api, Fixtuur} from '@fixtuur/backpack';
import * as Core from '../../store/core';

export const getInspirationProductSelections = (
    productId: string,
    productType: string,
    configuratorDefinition: ConfiguratorDefinition,
    dispatch: Dispatch<any>,
): Fixtuur.Configuration.Selections.Selections => {
    const productProperty: string | undefined = configuratorDefinition.partsMap[productType]?.productProperty;
    if (productProperty === undefined) {
        dispatch(
            Core.error({
                message: `Product type "${productType}" missing in configurator definition "${configuratorDefinition.displayName}"
                \n"${productType}" will need to be added to the configurator definition.
                `,
                context: {
                    type: 'log',
                },
            }),
        );
    }
    return Fixtuur.Configuration.Selections.create({[productProperty]: productId});
};

/**
 * Takes an array of configurator definitions.
 * Returns an array of configurator definitions where the resulting configuration should:
 * a) Contain the inspiration starting product.
 * b) Have multiple parts.
 *
 * For more info, see RFC:
 * https://www.notion.so/fixtuur/RFC-08-Doras-definition-of-ready-for-add-parts-3043aaa8d8ff4f4296c2daa61007fc43
 *
 */
export const getMultiPartConfigurators = async (
    allConfiguratorDefinitions: ConfiguratorDefinition[],
    serviceConfig: Api.ServiceConfig,
    startingSelections: Fixtuur.Configuration.Selections.Selections,
    dispatch: Dispatch<any>,
): Promise<ConfiguratorDefinition[]> => {
    const getMaxPartsCountInConfiguration = (spaceId: string) =>
        pipe(
            Api.Configuration.getMaxPartsCount(
                serviceConfig,
                spaceId,
                Fixtuur.Configuration.Selections.toApi(startingSelections),
            ),
            TE.mapLeft(error =>
                dispatch(
                    Core.error({
                        message: `Error fetching parts count for spaceId: ${spaceId}: ${error.message}`,
                        context: {
                            type: 'log',
                        },
                    }),
                ),
            ),
            TE.matchW(
                () => null,
                maxPartsCount => ({spaceId, maxPartsCount}),
            ),
        )();

    // space ids for the configurations with multiple parts
    const validSpaceIds = await Promise.all(
        allConfiguratorDefinitions.map(configDef =>
            getMaxPartsCountInConfiguration(configDef.configurationSpaceId),
        ),
    ).then(spaceIds => spaceIds.filter(o => o && o.maxPartsCount > 1).map(o => o?.spaceId));

    return allConfiguratorDefinitions.filter(cd => validSpaceIds.includes(cd.configurationSpaceId));
};
